import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import styled from '@emotion/styled';
import { Popover, IconButton, InputAdornment, TextField, Menu, MenuItem } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';

export interface CustomDateTimePickerProps {
  /**
   * 일시 값:
   * - 단일 모드: 'YYYY-MM-DD HH:mm' (string 또는 [string])
   * - 기간 모드(isRange): [string, string] 또는 'YYYY-MM-DD HH:mm ~ YYYY-MM-DD HH:mm'
   */
  value?: string | string[] | [string, string] | null;
  /**
   * 일시 변경 콜백:
   * - 단일 모드: string 반환 (e.g. "2026-08-20 12:00")
   * - 기간 모드(isRange): [string, string] 2개 요소 배열 반환 (e.g. ["2026-08-20 09:00", "2026-08-25 18:00"])
   */
  onChange?:
    | ((value: string) => void)
    | ((value: [string, string]) => void)
    | ((value: any) => void);
  /**
   * 기간 선택 모드 활성화 여부
   */
  isRange?: boolean;
  /**
   * 에러 상태 여부 (true인 경우 border 및 에러 메시지를 red로 표시)
   */
  isError?: boolean;
  /**
   * input 아래에 표시할 에러 메시지 (red 색상)
   */
  ErrMsg?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  clearable?: boolean;
}

// -------------------------------------------------------------
// Styled Components (ELM Design System - No Border Radius except Day Circle)
// -------------------------------------------------------------
const PickerWrapper = styled.div<{ isRange?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 0px;
  overflow: hidden;
  user-select: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  box-sizing: border-box;
`;

const RangeTabBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const RangeTabBtn = styled.button<{ active?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 6px 8px;
  border-radius: 0px;
  border: 1px solid ${({ active }) => (active ? '#0284c7' : '#e2e8f0')};
  background-color: ${({ active }) => (active ? '#f0f9ff' : '#ffffff')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0284c7;
  }
`;

const RangeTabLabel = styled.span<{ active?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#0284c7' : '#64748b')};
`;

const RangeTabValue = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 2px;
`;

const AllDayBtn = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: 0px;
  border: 1px solid ${({ active }) => (active ? '#0284c7' : '#cbd5e1')};
  background-color: ${({ active }) => (active ? '#0284c7' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#334155')};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  align-self: stretch;

  &:hover {
    border-color: #0284c7;
    background-color: ${({ active }) => (active ? '#0369a1' : '#f0f9ff')};
    color: ${({ active }) => (active ? '#ffffff' : '#0284c7')};
  }
`;

const PickerBodyRow = styled.div`
  display: flex;
  height: 295px;
`;

const CalendarContainer = styled.div`
  width: 260px;
  padding: 12px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
`;

const HeaderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 2px;
`;

const YearMonthSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DropdownBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: transparent;
  border: none;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 0px;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: #f1f5f9;
  }
`;

const ArrowNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ArrowBtn = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #64748b;
  border-radius: 0px;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    background-color: #f1f5f9;
    color: #0f172a;
  }
`;

const DaysOfWeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 6px;
`;

const DayOfWeekLabel = styled.span<{
  isSunday?: boolean;
  isSaturday?: boolean;
}>`
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ isSunday, isSaturday }) =>
    isSunday ? '#ef4444' : isSaturday ? '#3b82f6' : '#334155'};
  padding: 2px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
`;

const DayCellWrapper = styled.div<{
  isInRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  isSameStartEnd?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 31px;
  position: relative;

  ${({ isInRange }) =>
    isInRange &&
    `
    background-color: #f1f5f9;
  `}

  ${({ isRangeStart, isSameStartEnd }) =>
    isRangeStart &&
    !isSameStartEnd &&
    `
    background: linear-gradient(to right, transparent 50%, #f1f5f9 50%);
  `}

  ${({ isRangeEnd, isSameStartEnd }) =>
    isRangeEnd &&
    !isSameStartEnd &&
    `
    background: linear-gradient(to left, transparent 50%, #f1f5f9 50%);
  `}
`;

// 달력 내부의 선택 부분만 동그라미 (border-radius: 50%) 유지
const DayButton = styled.button<{
  isCurrentMonth?: boolean;
  isSelected?: boolean;
}>`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  font-size: 12.5px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  background-color: ${({ isSelected }) => (isSelected ? '#333e48' : 'transparent')};
  color: ${({ isSelected, isCurrentMonth }) =>
    isSelected ? '#ffffff' : isCurrentMonth ? '#1e293b' : '#cbd5e1'};
  cursor: pointer;
  position: relative;
  z-index: 2;
  transition: all 0.12s ease;

  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? '#333e48' : '#e2e8f0')};
  }
`;

const TimeContainer = styled.div`
  width: 140px;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fafbfc;
  box-sizing: border-box;
`;

const TimeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
  flex-shrink: 0;
`;

const TimeScrollWrapper = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const TimeColumn = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  padding: 4px 2px;
  box-sizing: border-box;

  & + & {
    border-left: 1px solid #edf2f7;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 0px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const TimeBtn = styled.button<{ isSelected?: boolean }>`
  width: 100%;
  height: 26px;
  border: none;
  border-radius: 0px;
  background-color: ${({ isSelected }) => (isSelected ? '#333e48' : 'transparent')};
  color: ${({ isSelected }) => (isSelected ? '#ffffff' : '#475569')};
  font-size: 12px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  cursor: pointer;
  margin: 1px 0;
  transition: all 0.12s ease;

  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? '#333e48' : '#e2e8f0')};
  }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 10px;
  border-top: 1px solid #e2e8f0;
  background-color: #ffffff;
  flex-shrink: 0;
`;

const ActionBtn = styled.button<{ primary?: boolean }>`
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 0px;
  cursor: pointer;
  transition: all 0.12s ease;

  ${({ primary }) =>
    primary
      ? `
    background-color: #333e48;
    color: #ffffff;
    border: none;
    &:hover {
      background-color: #1e293b;
    }
  `
      : `
    background-color: transparent;
    color: #64748b;
    border: 1px solid #cbd5e1;
    &:hover {
      background-color: #f1f5f9;
      color: #0f172a;
    }
  `}
`;

// -------------------------------------------------------------
// Component Implementation
// -------------------------------------------------------------
export const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  value,
  onChange,
  isRange = false,
  isError,
  ErrMsg,
  label,
  placeholder,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  required = false,
  clearable = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const hasError = isError !== undefined ? isError : error;
  const errorMessage = ErrMsg !== undefined ? ErrMsg : helperText;

  const defaultPlaceholder = isRange ? 'YYYY.MM.DD HH:mm ~ YYYY.MM.DD HH:mm' : 'YYYY.MM.DD HH:mm';

  // 날짜 문자열/배열 파싱 헬퍼 (초기값이 없으면 start/end를 null로 반환)
  const parseDateTimeInput = (
    valInput?: string | string[] | [string, string] | null
  ): {
    start: Dayjs | null;
    end: Dayjs | null;
    hasValue: boolean;
  } => {
    if (!valInput) {
      return { start: null, end: null, hasValue: false };
    }

    if (Array.isArray(valInput)) {
      const hasAny = valInput.some((v) => Boolean(v && v.trim()));
      if (!hasAny) {
        return { start: null, end: null, hasValue: false };
      }
      const startStr = (valInput[0] || '').replace(/\./g, '-').replace('T', ' ');
      const endStr = (valInput[1] || valInput[0] || '').replace(/\./g, '-').replace('T', ' ');
      const s = dayjs(startStr);
      const e = dayjs(endStr);
      const validStart = s.isValid() ? s : null;
      const validEnd = e.isValid() ? e : validStart;
      return {
        start: validStart,
        end: validEnd,
        hasValue: Boolean(validStart),
      };
    }

    if (typeof valInput === 'string') {
      const trimmed = valInput.trim();
      if (!trimmed) {
        return { start: null, end: null, hasValue: false };
      }
      if (isRange && trimmed.includes('~')) {
        const [startPart, endPart] = trimmed.split('~').map((s) => s.trim());
        const s = dayjs(startPart.replace(/\./g, '-').replace('T', ' '));
        const e = dayjs(endPart.replace(/\./g, '-').replace('T', ' '));
        const validStart = s.isValid() ? s : null;
        const validEnd = e.isValid() ? e : validStart;
        return {
          start: validStart,
          end: validEnd,
          hasValue: Boolean(validStart),
        };
      }
      const clean = trimmed.replace(/\./g, '-').replace('T', ' ');
      const s = dayjs(clean);
      const validStart = s.isValid() ? s : null;
      return {
        start: validStart,
        end: validStart,
        hasValue: Boolean(validStart),
      };
    }

    return { start: null, end: null, hasValue: false };
  };

  const initial = parseDateTimeInput(value);

  // 캘린더 월 탐색 기준일 (초기값이 없으면 오늘 기준)
  const [viewDate, setViewDate] = useState<Dayjs>(initial.start || dayjs());
  const [activeTarget, setActiveTarget] = useState<'start' | 'end'>('start');

  // 시작일시 상태 (초기값 없으면 null)
  const [startDate, setStartDate] = useState<Dayjs | null>(initial.start);
  const [startHour, setStartHour] = useState<number>(initial.start ? initial.start.hour() : 9);
  const [startMinute, setStartMinute] = useState<number>(
    initial.start ? initial.start.minute() : 0
  );
  const [startSecond, setStartSecond] = useState<number>(
    initial.start ? initial.start.second() : 0
  );

  // 종료일시 상태 (기간 모드용, 초기값 없으면 null)
  const [endDate, setEndDate] = useState<Dayjs | null>(initial.end);
  const [endHour, setEndHour] = useState<number>(initial.end ? initial.end.hour() : 18);
  const [endMinute, setEndMinute] = useState<number>(initial.end ? initial.end.minute() : 0);
  const [endSecond, setEndSecond] = useState<number>(initial.end ? initial.end.second() : 0);

  // Year & Month Dropdown Menu State
  const [yearMenuAnchor, setYearMenuAnchor] = useState<HTMLElement | null>(null);
  const [monthMenuAnchor, setMonthMenuAnchor] = useState<HTMLElement | null>(null);

  const hourListRef = useRef<HTMLDivElement | null>(null);
  const minuteListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const { start, end } = parseDateTimeInput(value);
    if (start) {
      setViewDate(start);
      setStartDate(start);
      setStartHour(start.hour());
      setStartMinute(start.minute());
      setStartSecond(start.second());
    } else {
      setStartDate(null);
      setStartHour(9);
      setStartMinute(0);
      setStartSecond(0);
    }

    if (end) {
      setEndDate(end);
      setEndHour(end.hour());
      setEndMinute(end.minute());
      setEndSecond(end.second());
    } else {
      setEndDate(null);
      setEndHour(18);
      setEndMinute(0);
      setEndSecond(0);
    }
  }, [value, isRange]);

  const isOpen = Boolean(anchorEl);

  const currentHour = activeTarget === 'start' ? startHour : endHour;
  const currentMinute = activeTarget === 'start' ? startMinute : endMinute;

  // Auto-scroll selected hour and minute into view when opened or activeTarget switched
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourListRef.current) {
          const selectedHourBtn =
            hourListRef.current.querySelector<HTMLButtonElement>("[data-selected='true']");
          if (selectedHourBtn) {
            selectedHourBtn.scrollIntoView({ block: 'center' });
          }
        }
        if (minuteListRef.current) {
          const selectedMinBtn =
            minuteListRef.current.querySelector<HTMLButtonElement>("[data-selected='true']");
          if (selectedMinBtn) {
            selectedMinBtn.scrollIntoView({ block: 'center' });
          }
        }
      }, 50);
    }
  }, [isOpen, activeTarget]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    const { start, end } = parseDateTimeInput(value);
    setViewDate(start || dayjs());
    setStartDate(start);
    setStartHour(start ? start.hour() : 9);
    setStartMinute(start ? start.minute() : 0);
    setStartSecond(start ? start.second() : 0);
    setEndDate(end);
    setEndHour(end ? end.hour() : 18);
    setEndMinute(end ? end.minute() : 0);
    setEndSecond(end ? end.second() : 0);
    setActiveTarget('start');
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setYearMenuAnchor(null);
    setMonthMenuAnchor(null);
  };

  const handleSelectDay = (d: Dayjs) => {
    if (!isRange) {
      setStartDate(d);
      return;
    }

    if (activeTarget === 'start') {
      setStartDate(d);
      if (endDate && d.isAfter(endDate)) {
        setEndDate(d);
      }
      setActiveTarget('end');
    } else {
      if (!startDate || d.isBefore(startDate)) {
        setStartDate(d);
      } else {
        setEndDate(d);
      }
    }
  };

  const handleSelectHour = (h: number) => {
    if (activeTarget === 'start') {
      setStartHour(h);
      setStartSecond(0);
      if (!startDate) setStartDate(viewDate);
    } else {
      setEndHour(h);
      if (h === 23 && endMinute === 59) {
        setEndSecond(59);
      } else {
        setEndSecond(0);
      }
      if (!endDate) setEndDate(startDate || viewDate);
    }
  };

  const handleSelectMinute = (m: number) => {
    if (activeTarget === 'start') {
      setStartMinute(m);
      setStartSecond(0);
      if (!startDate) setStartDate(viewDate);
    } else {
      setEndMinute(m);
      if (endHour === 23 && m === 59) {
        setEndSecond(59);
      } else {
        setEndSecond(0);
      }
      if (!endDate) setEndDate(startDate || viewDate);
    }
  };

  // 종일 상태 판별 (시작 00:00:00, 종료 23:59:59)
  const isAllDay = Boolean(
    startDate &&
      endDate &&
      startHour === 0 &&
      startMinute === 0 &&
      endHour === 23 &&
      endMinute === 59
  );

  // '종일' 클릭 핸들러
  const handleSelectAllDay = () => {
    let targetStart = startDate;
    let targetEnd = endDate;

    if (!targetStart && !targetEnd) {
      // 2. 날짜가 선택되지 않은 경우: 오늘 기준
      const today = dayjs();
      targetStart = today;
      targetEnd = today;
      setViewDate(today);
    } else if (targetStart && !targetEnd) {
      // 시작일만 있는 경우: 시작일 당일
      targetEnd = targetStart;
    } else if (!targetStart && targetEnd) {
      targetStart = targetEnd;
    }
    // 3. 시작일과 종료일이 모두 있는 경우: targetStart, targetEnd 기간 유지

    setStartDate(targetStart);
    setEndDate(targetEnd);
    setStartHour(0);
    setStartMinute(0);
    setStartSecond(0);
    setEndHour(23);
    setEndMinute(59);
    setEndSecond(59);
  };

  const handleApply = () => {
    if (isRange) {
      if (!startDate && !endDate) {
        onChange?.(['', ''] as any);
        handleClose();
        return;
      }
      const baseStart = startDate || viewDate;
      const baseEnd = endDate || baseStart;

      const finalStart = baseStart
        .hour(startHour)
        .minute(startMinute)
        .second(startSecond)
        .format('YYYY-MM-DD HH:mm');
      const finalEnd = baseEnd
        .hour(endHour)
        .minute(endMinute)
        .second(endSecond)
        .format('YYYY-MM-DD HH:mm');
      onChange?.([finalStart, finalEnd] as any);
    } else {
      if (!startDate) {
        onChange?.('' as any);
        handleClose();
        return;
      }
      const finalDt = startDate
        .hour(startHour)
        .minute(startMinute)
        .second(startSecond)
        .format('YYYY-MM-DD HH:mm');
      onChange?.(finalDt as any);
    }
    handleClose();
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setStartDate(null);
    setEndDate(null);
    if (isRange) {
      onChange?.(['', ''] as any);
    } else {
      onChange?.('' as any);
    }
    handleClose();
  };

  // Calendar Grid Calculation
  const startOfMonth = viewDate.startOf('month');
  const startGrid = startOfMonth.startOf('week');
  const endGrid = viewDate.endOf('month').endOf('week');

  const calendarDays: Dayjs[] = [];
  let cur = startGrid;
  while (cur.isBefore(endGrid) || cur.isSame(endGrid, 'day')) {
    calendarDays.push(cur);
    cur = cur.add(1, 'day');
  }

  // Hours (00~23) & Minutes (00~59, 1m intervals)
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // Years for Year Menu
  const currentYear = viewDate.year();
  const yearList = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  const monthList = Array.from({ length: 12 }, (_, i) => i + 1);

  // 날짜가 '', null, undefined인 경우 빈 값 처리
  let displayString = '';
  if (value) {
    if (Array.isArray(value)) {
      const hasAny = value.some((v) => Boolean(v && v.trim()));
      if (hasAny) {
        if (isRange) {
          const s = value[0] ? value[0].replace(/T/g, ' ').slice(0, 16) : '';
          const e = value[1] ? value[1].replace(/T/g, ' ').slice(0, 16) : '';
          displayString = s && e ? `${s} ~ ${e}` : s || e || '';
        } else {
          displayString = value[0] ? value[0].replace(/T/g, ' ').slice(0, 16) : '';
        }
      }
    } else if (typeof value === 'string' && value.trim()) {
      displayString = value.replace(/T/g, ' ');
    }
  }

  return (
    <>
      <TextField
        label={label}
        value={displayString}
        placeholder={placeholder || defaultPlaceholder}
        disabled={disabled}
        error={hasError}
        helperText={hasError ? errorMessage : ''}
        fullWidth={fullWidth}
        required={required}
        onClick={handleOpen}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '32px',
            fontSize: '13px',
            borderRadius: '0px',
            paddingRight: '6px',
            fontWeight: '400 !important',
            ...(hasError && {
              '& fieldset': {
                borderColor: '#ef4444 !important',
              },
              '&:hover fieldset': {
                borderColor: '#dc2626 !important',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ef4444 !important',
              },
            }),
          },
          '& .MuiOutlinedInput-input': {
            height: '32px',
            boxSizing: 'border-box',
            padding: '4px 8px',
          },
          '& .MuiInputLabel-root': {
            fontSize: '13px',
            ...(hasError && {
              color: '#ef4444 !important',
            }),
          },
          '& .MuiFormHelperText-root': {
            fontSize: '11.5px',
            color: '#ef4444 !important',
            marginLeft: '2px',
            marginTop: '3px',
          },
        }}
        InputProps={{
          readOnly: true,
          style: {
            cursor: disabled ? 'default' : 'pointer',
            fontSize: '13px',
          },
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: '#64748b' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {clearable && displayString && !disabled && (
                <IconButton size="small" onClick={handleClear} edge="end" sx={{ p: 0.5 }}>
                  <ClearIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
              borderRadius: '0px',
              border: '1px solid #e2e8f0',
              maxHeight: 'none !important',
              maxWidth: 'none',
              overflow: 'visible',
            },
          },
        }}
      >
        <PickerWrapper isRange={isRange}>
          {/* 기간(isRange) 모드일 때 상단 시작/종료 탭 헤더 */}
          {isRange && (
            <RangeTabBar>
              <RangeTabBtn
                active={activeTarget === 'start'}
                onClick={() => setActiveTarget('start')}
              >
                <RangeTabLabel active={activeTarget === 'start'}>● 시작일시</RangeTabLabel>
                <RangeTabValue>
                  {startDate ? (
                    <>
                      {startDate.format('YYYY.MM.DD')} {String(startHour).padStart(2, '0')}:
                      {String(startMinute).padStart(2, '0')}
                    </>
                  ) : (
                    <span style={{ color: '#94a3b8', fontWeight: 400 }}>시작일 선택</span>
                  )}
                </RangeTabValue>
              </RangeTabBtn>

              <span style={{ color: '#94a3b8', fontWeight: 700 }}>~</span>

              <RangeTabBtn active={activeTarget === 'end'} onClick={() => setActiveTarget('end')}>
                <RangeTabLabel active={activeTarget === 'end'}>● 종료일시</RangeTabLabel>
                <RangeTabValue>
                  {endDate ? (
                    <>
                      {endDate.format('YYYY.MM.DD')} {String(endHour).padStart(2, '0')}:
                      {String(endMinute).padStart(2, '0')}
                    </>
                  ) : (
                    <span style={{ color: '#94a3b8', fontWeight: 400 }}>종료일 선택</span>
                  )}
                </RangeTabValue>
              </RangeTabBtn>

              <AllDayBtn
                type="button"
                active={isAllDay}
                onClick={handleSelectAllDay}
                title="종일 (00:00:00 ~ 23:59:59)"
              >
                종일
              </AllDayBtn>
            </RangeTabBar>
          )}

          <PickerBodyRow>
            {/* 1. Date Calendar */}
            <CalendarContainer>
              {/* Header: [2026 ▾] [8월 ▾]       [<] [>] */}
              <HeaderNav>
                <YearMonthSelectors>
                  <DropdownBtn onClick={(e) => setYearMenuAnchor(e.currentTarget)}>
                    <span>{viewDate.format('YYYY')}</span>
                    <ArrowDropDownIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </DropdownBtn>

                  <DropdownBtn onClick={(e) => setMonthMenuAnchor(e.currentTarget)}>
                    <span>{viewDate.format('M월')}</span>
                    <ArrowDropDownIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </DropdownBtn>
                </YearMonthSelectors>

                <ArrowNavGroup>
                  <ArrowBtn
                    onClick={() => setViewDate((prev) => prev.subtract(1, 'month'))}
                    title="이전 달"
                  >
                    <ChevronLeftIcon sx={{ fontSize: 19 }} />
                  </ArrowBtn>
                  <ArrowBtn
                    onClick={() => setViewDate((prev) => prev.add(1, 'month'))}
                    title="다음 달"
                  >
                    <ChevronRightIcon sx={{ fontSize: 19 }} />
                  </ArrowBtn>
                </ArrowNavGroup>
              </HeaderNav>

              {/* Year Dropdown Menu */}
              <Menu
                anchorEl={yearMenuAnchor}
                open={Boolean(yearMenuAnchor)}
                onClose={() => setYearMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: { maxHeight: 220, borderRadius: '0px' },
                  },
                }}
              >
                {yearList.map((yr) => (
                  <MenuItem
                    key={yr}
                    selected={yr === viewDate.year()}
                    onClick={() => {
                      setViewDate((prev) => prev.year(yr));
                      setYearMenuAnchor(null);
                    }}
                    sx={{ fontSize: '13px' }}
                  >
                    {yr}년
                  </MenuItem>
                ))}
              </Menu>

              {/* Month Dropdown Menu */}
              <Menu
                anchorEl={monthMenuAnchor}
                open={Boolean(monthMenuAnchor)}
                onClose={() => setMonthMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: { maxHeight: 220, borderRadius: '0px' },
                  },
                }}
              >
                {monthList.map((mo) => (
                  <MenuItem
                    key={mo}
                    selected={mo === viewDate.month() + 1}
                    onClick={() => {
                      setViewDate((prev) => prev.month(mo - 1));
                      setMonthMenuAnchor(null);
                    }}
                    sx={{ fontSize: '13px' }}
                  >
                    {mo}월
                  </MenuItem>
                ))}
              </Menu>

              {/* Days of Week (일, 월, 화, 수, 목, 금, 토) */}
              <DaysOfWeekHeader>
                <DayOfWeekLabel isSunday>일</DayOfWeekLabel>
                <DayOfWeekLabel>월</DayOfWeekLabel>
                <DayOfWeekLabel>화</DayOfWeekLabel>
                <DayOfWeekLabel>수</DayOfWeekLabel>
                <DayOfWeekLabel>목</DayOfWeekLabel>
                <DayOfWeekLabel>금</DayOfWeekLabel>
                <DayOfWeekLabel isSaturday>토</DayOfWeekLabel>
              </DaysOfWeekHeader>

              {/* Calendar Days Grid */}
              <DaysGrid>
                {calendarDays.map((d) => {
                  const isCurrentMonth = d.month() === viewDate.month();

                  const isStart = Boolean(startDate && d.isSame(startDate, 'day'));
                  const isEnd = Boolean(isRange && endDate && d.isSame(endDate, 'day'));
                  const isSelected = isStart || isEnd;

                  const isInRange = Boolean(
                    isRange &&
                      startDate &&
                      endDate &&
                      d.isAfter(startDate, 'day') &&
                      d.isBefore(endDate, 'day')
                  );

                  const isSameStartEnd = Boolean(
                    isRange && startDate && endDate && startDate.isSame(endDate, 'day')
                  );

                  return (
                    <DayCellWrapper
                      key={d.format('YYYY-MM-DD')}
                      isInRange={isInRange}
                      isRangeStart={isStart}
                      isRangeEnd={isEnd}
                      isSameStartEnd={isSameStartEnd}
                    >
                      <DayButton
                        isCurrentMonth={isCurrentMonth}
                        isSelected={isSelected}
                        onClick={() => handleSelectDay(d)}
                      >
                        {d.date()}
                      </DayButton>
                    </DayCellWrapper>
                  );
                })}
              </DaysGrid>
            </CalendarContainer>

            {/* 2. Time Selector (Hour & Minute) */}
            <TimeContainer>
              <TimeHeader>
                <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
                <span>
                  {isRange ? `${activeTarget === 'start' ? '시작' : '종료'} 시간` : '시간 선택'}
                </span>
              </TimeHeader>

              <TimeScrollWrapper>
                {/* 시 (00~23) */}
                <TimeColumn ref={hourListRef}>
                  {hours.map((h) => (
                    <TimeBtn
                      key={`h-${h}`}
                      data-selected={currentHour === h ? 'true' : 'false'}
                      isSelected={currentHour === h}
                      onClick={() => handleSelectHour(h)}
                    >
                      {String(h).padStart(2, '0')}시
                    </TimeBtn>
                  ))}
                </TimeColumn>

                {/* 분 (00~55) */}
                <TimeColumn ref={minuteListRef}>
                  {minutes.map((m) => (
                    <TimeBtn
                      key={`m-${m}`}
                      data-selected={currentMinute === m ? 'true' : 'false'}
                      isSelected={currentMinute === m}
                      onClick={() => handleSelectMinute(m)}
                    >
                      {String(m).padStart(2, '0')}분
                    </TimeBtn>
                  ))}
                </TimeColumn>
              </TimeScrollWrapper>

              <FooterBar>
                <ActionBtn onClick={handleClose}>취소</ActionBtn>
                <ActionBtn primary onClick={handleApply}>
                  확인
                </ActionBtn>
              </FooterBar>
            </TimeContainer>
          </PickerBodyRow>
        </PickerWrapper>
      </Popover>
    </>
  );
};

export default CustomDateTimePicker;
